import { useState } from 'react';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import { GridRowModes, GridToolbarContainer, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';

import { dataGridPremiumColumns } from '../../../utils/helper';
import {
  DataGridIdentifiers,
  FormErrorUI,
  InputUI,
  AddButtonUi,
  OrderArrowsUI,
  CheckboxUI,
  DatagridPremiumUi,
} from '../../Interface';

const EditToolbar = (props) => {
  const { disabled, push, defaultField } = props;
  const { values, validateField } = useFormikContext();

  const handleClick = async () => {
    const newVisOrder = (values.checkListTemplateItem?.length || 0) + 1;

    defaultField.id = uuidv4();
    defaultField.visOrder = newVisOrder;
    await push(defaultField);

    const fieldsToValidate = [
      `checkListTemplateItem.${values.checkListTemplateItem.length ?? 0}.name`,
      `checkListTemplateItem.${values.checkListTemplateItem.length ?? 0}.attachRequired`,
      `checkListTemplateItem.${values.checkListTemplateItem.length ?? 0}.descriptionRequired`,
      `checkListTemplateItem.${values.checkListTemplateItem.length ?? 0}.descriptionName`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }
  };

  return (
    <GridToolbarContainer>
      <AddButtonUi disabled={disabled} onClick={handleClick} />
    </GridToolbarContainer>
  );
};

const CheckListTemplateItem = ({ user, disabled, defaultField }) => {
  const apiRef = useGridApiRef();
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  const getValueIndex = (values, id) => {
    return values.checkListTemplateItem.findIndex((rc) => rc.id === id);
  };

  const handleOrderChange = (id, currentOrder, direction) => {
    const newOrder = direction === 'up' ? currentOrder - 1 : currentOrder + 1;

    if (newOrder < 1 || newOrder > values.checkListTemplateItem.length) {
      return;
    }

    const updatedList = [...values.checkListTemplateItem]
      .map((item) => {
        if (item.id === id) {
          return { ...item, visOrder: newOrder };
        } else if (item.visOrder === newOrder) {
          return { ...item, visOrder: currentOrder };
        }
        return item;
      })
      .sort((a, b) => a.visOrder - b.visOrder);

    setFieldValue('checkListTemplateItem', updatedList);
  };

  const columns = (setFieldValue, validateField, errors, remove) => {
    return dataGridPremiumColumns([
      {
        field: 'name',
        headerName: 'Elemek',
        type: 'string',
        editable: true,
        sortable: false,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`checkListTemplateItem.${valueIndex}.name`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.checkListTemplateItem?.[valueIndex]?.name} />}
              onChange={(e) => {
                setFieldValue(`checkListTemplateItem.${valueIndex}.name`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`checkListTemplateItem.${valueIndex}.name`)}
            />
          );
        },
      },
      {
        field: 'attachRequired',
        type: 'boolean',
        headerName: 'Csatolt fájlt kötelező',
        editable: true,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return (
            <Field
              name={`checkListTemplateItem.${valueIndex}.attachRequired`}
              component={CheckboxUI}
              helperText={<FormErrorUI message={errors?.checkListTemplateItem?.[valueIndex]?.attachRequired} />}
              checked={data.row?.attachRequired === true}
              variant="standard"
              onChange={(e) => {
                setFieldValue(`checkListTemplateItem.${valueIndex}.attachRequired`, e.target.checked ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.checked,
                });
              }}
              onBlur={async () => await validateField(`checkListTemplateItem.${valueIndex}.attachRequired`)}
            />
          );
        },
      },
      {
        field: 'descriptionRequired',
        type: 'boolean',
        headerName: 'Leírás kötelező',
        editable: true,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          return (
            <Field
              name={`checkListTemplateItem.${valueIndex}.descriptionRequired`}
              component={CheckboxUI}
              helperText={<FormErrorUI message={errors?.checkListTemplateItem?.[valueIndex]?.descriptionRequired} />}
              checked={data.row?.descriptionRequired === true}
              variant="standard"
              onChange={(e) => {
                setFieldValue(`checkListTemplateItem.${valueIndex}.descriptionRequired`, e.target.checked ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.checked,
                });
              }}
              onBlur={async () => await validateField(`checkListTemplateItem.${valueIndex}.descriptionRequired`)}
            />
          );
        },
      },
      {
        field: 'descriptionName',
        headerName: 'Leírás megnevezés',
        flex: 1,
        editable: true,
        sortable: false,
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`checkListTemplateItem.${valueIndex}.descriptionName`}
              type="text"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.checkListTemplateItem?.[valueIndex]?.descriptionName} />}
              onChange={(e) => {
                setFieldValue(`checkListTemplateItem.${valueIndex}.descriptionName`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`checkListTemplateItem.${valueIndex}.descriptionName`)}
            />
          );
        },
      },
      {
        field: 'visOrder',
        headerName: 'Sorrend',
        renderCell: (params) => {
          const handleUpClick = () => handleOrderChange(params.row.id, params.row.visOrder, 'up');
          const handleDownClick = () => handleOrderChange(params.row.id, params.row.visOrder, 'down');
          return <OrderArrowsUI onUpClick={handleUpClick} onDownClick={handleDownClick} disabled={disabled} />;
        },
        sortable: false,
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id }) => {
          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={() => {
                const valueIndex = getValueIndex(values, id);
                return remove(valueIndex);
              }}
              color="inherit"
              disabled={disabled}
            />,
          ];
        },
      },
    ]);
  };

  return (
    <div className="overflow-x-auto">
      <div className="align-middle">
        <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg mb-5 px-3">
          <FieldArray name="checkListTemplateItem">
            {({ remove, push }) => (
              <DatagridPremiumUi
                apiRef={apiRef}
                datagridIdentifier={DataGridIdentifiers.checkListTemplateItem}
                height={600}
                columns={columns(setFieldValue, validateField, errors, remove)}
                rows={values?.checkListTemplateItem ?? []}
                slots={{
                  toolbar: EditToolbar,
                }}
                headerFilters={false}
                slotProps={{
                  toolbar: {
                    disabled,
                    push,
                    defaultField,
                  },
                }}
                onCellDoubleClick={(_params, event) => {
                  event.defaultMuiPrevented = true;
                }}
                editMode="row"
                getRowHeight={() => 'auto'}
                onCellClick={(params, event) => {
                  if (disabled) return;
                  const rowMode = apiRef.current.getRowMode(params.row.id);
                  if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                }}
              />
            )}
          </FieldArray>
        </div>
      </div>
    </div>
  );
};

export default CheckListTemplateItem;
